/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { Text, TouchableOpacity, View } from 'react-native';

import { operationalFilterStyles as opChipStyles } from '../../shared/filters/public-api';
import { csvImportExportModalStyles as styles } from './csvImportExportModal.styles';
import type { PreviewPageSize } from './hooks/csvPreviewHelpers';

type CsvPreviewPaginationProps = {
  pageIndex: number;
  pageSize: number;
  totalCount: number;
  pageSizeOptions: readonly PreviewPageSize[];
  onPageSizeChange: (size: PreviewPageSize) => void;
  onPrevious: () => void;
  onNext: () => void;
  compact: boolean;
};

export const CsvPreviewPagination = ({
  pageIndex,
  pageSize,
  totalCount,
  pageSizeOptions,
  onPageSizeChange,
  onPrevious,
  onNext,
  compact,
}: CsvPreviewPaginationProps) => {
  const minPageSize = pageSizeOptions[0] ?? 10;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const pageNum = pageIndex + 1;
  const from = totalCount === 0 ? 0 : pageIndex * pageSize + 1;
  const to = Math.min(totalCount, (pageIndex + 1) * pageSize);
  const prevDisabled = pageIndex === 0;
  const nextDisabled = (pageIndex + 1) * pageSize >= totalCount;
  const needsPaging = totalCount > pageSize;

  // Pocas filas: no vale la pena el bloque de paginación.
  if (totalCount === 0 || totalCount <= minPageSize) return null;

  return (
    <View style={styles.csvPagerBar} accessibilityLabel={`Página ${pageNum} de ${totalPages}`}>
      <Text style={styles.csvPagerSummary} accessibilityLabel={`Filas ${from} a ${to} de ${totalCount}`}>
        {from}–{to} de {totalCount}
      </Text>
      <View style={styles.csvPagerChips}>
        {pageSizeOptions.map((n) => {
          const selected = pageSize === n;
          return (
            <TouchableOpacity
              key={n}
              style={[opChipStyles.chip, selected && opChipStyles.chipActive, styles.csvPagerChip]}
              onPress={() => onPageSizeChange(n)}
              accessibilityRole="button"
              accessibilityState={{ selected }}
              accessibilityLabel={`Filas por página: ${n}`}
            >
              <Text style={[opChipStyles.chipText, selected && opChipStyles.chipTextActive]}>{n}</Text>
            </TouchableOpacity>
          );
        })}
      </View>
      {needsPaging ? (
        <View style={styles.csvPagerNavInline}>
          <TouchableOpacity
            style={[styles.csvPagerNavBtn, prevDisabled && styles.csvPagerBtnDisabled]}
            onPress={onPrevious}
            disabled={prevDisabled}
            accessibilityLabel="Página anterior"
          >
            <Text style={styles.csvPagerBtnText}>{compact ? '‹' : 'Ant.'}</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.csvPagerNavBtn, nextDisabled && styles.csvPagerBtnDisabled]}
            onPress={onNext}
            disabled={nextDisabled}
            accessibilityLabel="Página siguiente"
          >
            <Text style={styles.csvPagerBtnText}>{compact ? '›' : 'Sig.'}</Text>
          </TouchableOpacity>
        </View>
      ) : null}
    </View>
  );
};
